<script setup lang="ts">
//#region imports
import { nextTick, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { omit } from 'lodash-es';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { useApi } from '@/api';
import { usePermissions } from '@/composables/usePermissions';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useUpdateUrl } from '@/composables/useUpdateUrl';
import { useLodash } from '@/setup/lodash';
import type UiPagination from '@/components/UIElements/UiPagination.vue';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type { SettingsPageQuery } from '@/types/core';
import type { GetDomainsManagementServiceRotationGroupsData, GetDomainsManagementServiceRotationGroupsResponse } from '@/types/openapi-ts';
//#endregion

//#region types
type RotationGroupsQuery = SettingsPageQuery<GetDomainsManagementServiceRotationGroupsData['query']>;
//#endregion

//#region const
const LS_PATH = 'RotationGroups';
const sizes = {
  1080: 20,
  1440: 30,
};
const domains = {};

const fields: UiTableNewField<GetDomainsManagementServiceRotationGroupsResponse['payload'][number]>[] = [
  {
    name: 'incrementing_id',
    sortable: 'custom',
    align: 'left',
    width: '60',
  },
  {
    name: 'name',
    label: 'group_name',
    sortable: 'custom',
    minWidth: '100',
  },
  {
    name: 'affiliates_using_group_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
    computedClass: () => isAffiliatesFilter.value ? 't:cursor-pointer positive' : '',
    click: handleRotationAffiliates,
  },
  {
    name: 'type',
    sortable: 'custom',
    computedValue: (row) => t(`domainManagement.fields.type.values.${row.type}`),
  },
  {
    name: 'dns_records_state',
    sortable: 'custom',
    type: 'status',
    statusText: (value) => t(`domainManagement.fields.dns_records_state.values.${value}`),
    status: (value) => value,
  },
  {
    name: 'ssl_certificate_state',
    sortable: 'custom',
    type: 'status',
    statusText: (value) => t(`domainManagement.fields.ssl_certificate_state.values.${value}`),
    status: (value) => value,
  },
  {
    name: 'reachability_state',
    sortable: 'custom',
    type: 'status',
    statusText: (value) => t(`domainManagement.fields.reachability_state.values.${value}`),
    status: (value) => value,
  },
  {
    name: 'traffic_last_30_days_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
  },
  {
    name: 'traffic_last_7_days_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
  },
  {
    name: 'traffic_last_day_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
  },
  {
    name: 'created_at',
    sortable: 'custom',
    width: '120',
    format: 'date-time',
  },
];
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const { hasPermission } = usePermissions();
const { t } = useI18n();
const updateUrl = useUpdateUrl();
const _ = useLodash();
const { searchQuery } = useTableSearch();
const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'incrementing_id',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});
const popupManager = usePopupManager();
//#endregion

//#region state
const tableData = ref<GetDomainsManagementServiceRotationGroupsResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
//#endregion

//#region computed
const canEditRotationGroups = computed(() => {
  return hasPermission('service_rotation_groups_edit');
});

const isAffiliatesFilter = computed(() => {
  return hasPermission('affiliates_view');
});
//#endregion

//#region methods
function initData() {
  const _urlData = updateUrl.getParseParams(route.hash);

  if (!_.isEmpty(_urlData)) {
    setPage(_urlData);

    delete _urlData.page;
    delete _urlData.limit;

    _.merge(optionsSort.value, _urlData);
  }
  nextTick(() => {
    fetchData();
  });
}

function _query() {
  return {
    ...optionsSort.value,
    limit: limit.value,
    search: searchQuery.value,
    offset: limit.value * page.value - limit.value,
  } as GetDomainsManagementServiceRotationGroupsData['query'];
}

function _completedFilterUrl() {
  const _dataFilters: RotationGroupsQuery = omit(_query(), 'offset');
  _dataFilters.page = page.value;
  updateUrl.updateGetParams(_dataFilters);
}

async function fetchData(silent = true) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  const options = _query();

  try {
    const { data: { payload, misc } } = await api.getRotationGroups(options);

    count.value = misc!.count;
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

async function openRotationGroupPopup(id = null) {
  popupManager.rotationGroupPopup.open({
    data: {
      groupId: id,
    },
    listeners: {
      success: () => {
        fetchData(true);
      },
    },
  });
}

function handleDelete(row: GetDomainsManagementServiceRotationGroupsResponse['payload'][number]) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('domainManagement.buttons.delete'),
      message: t('domainManagement.messages.delete', { name: row.name}),
      bodyIcon: 'trash-alt',
      confirmIcon: 'trash-alt',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('domainManagement.buttons.delete'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.deleteRotationGroup(row.id);
          await fetchData();
        } catch (error) {
          window.console.error(error);
        }
        context.handleClose();
      },
    },
  });
}

function handleRotationAffiliates(row: GetDomainsManagementServiceRotationGroupsResponse['payload'][number]) {
  if (!isAffiliatesFilter.value) return;
  popupManager.rotationGroupAffiliatesPopup.open({
    data: {
      groupId: row.id,
      groupName: row.name,
    },
  });
}

function handleRefresh () {
  page.value = 1;
  fetchData();
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="rotation-groups">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('domainManagement.pageTitle.rotationGroups') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          v-if="canEditRotationGroups"
          filled
          icon="plus"
          @click="openRotationGroupPopup()"
        >
          {{ $t('domainManagement.buttons.add_rotation_group') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="domainManagement"
      @sort="handleSort"
    >
      <template
        v-if="canEditRotationGroups"
        v-slot:append
      >
        <ElTableColumn
          align="center"
          width="76"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              :tooltip="$t('domainManagement.buttons.edit')"
              icon="pen"
              @click="openRotationGroupPopup(scope.row.id)"
            />
            <ActionIcon
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('domainManagement.buttons.delete')"
              :disabled="scope.row.affiliates_using_group_count > 0"
              @click="handleDelete(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>

